<div class="mailbox-page-container">
  <div class="mailbox-page-header">
    <div class="mailbox-page-header-left">
      <h1 class="mailbox-page-title">Mailbox</h1>
    </div>
  </div>

  <!-- Tab Navigation -->
  <div class="mailbox-tabs">
    <button
      class="tab-btn"
      [class.active]="activeTab === 'configurations'"
      (click)="switchTab('configurations')">
      Configurations
    </button>

    <button
      class="tab-btn"
      [class.active]="activeTab === 'connectors'"
      (click)="switchTab('connectors')">
      Connectors
    </button>
  </div>

  <!-- Tab Content -->
  <div class="tab-content">

    <!-- CONFIGURATIONS TAB -->
    <div *ngIf="activeTab === 'configurations'">

      <div class="mailbox-configurations-container">
        <div class="mailbox-configurations-header">
          <div class="mailbox-configurations-header-left">
            <h2 class="mailbox-configurations-title">Mailbox Configurations</h2>
          </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" *ngIf="configurationsLoading">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading mailbox connections...</p>
        </div>

        <!-- Error State -->
        <div class="error-state" *ngIf="!configurationsLoading && configurationsError">
          <div class="error-content">
            <div class="error-icon">&#9888;</div>
            <p class="error-message">{{ configurationsError }}</p>
            <button class="retry-button" (click)="loadConnections()">
              Retry
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state"
          *ngIf="!configurationsLoading && !configurationsError && connections.length === 0">
          <div class="empty-content">
            <div class="empty-icon">&#9993;</div>
            <h3 class="empty-title">No Mailbox Connections</h3>
            <p class="empty-message">
              You haven't connected any mailbox yet. Go to the Connectors tab to set up a connection.
            </p>
          </div>
        </div>

        <!-- Connections List -->
        <div
          class="connections-list"
          *ngIf="!configurationsLoading && !configurationsError && connections.length > 0">

          <div
            class="connection-card"
            *ngFor="let connection of connections">

            <div class="connection-card-header">
              <div class="connection-info">
                <div class="connection-avatar">
                  <span class="avatar-text">
                    {{ connection.emailAddress.charAt(0).toUpperCase() }}
                  </span>
                </div>

                <div class="connection-details">
                  <span class="connection-email">
                    {{ connection.emailAddress }}
                  </span>

                  <span class="connection-provider">
                    {{ connection.providerName }}
                  </span>
                </div>
              </div>

              <span
                class="status-badge"
                [class]="getStatusClass(connection.status)">
                {{ connection.status }}
              </span>
            </div>

            <div class="connection-card-body">
              <div class="connection-meta">

                <div class="meta-item">
                  <span class="meta-label">Connected</span>
                  <span class="meta-value">
                    {{ formatDate(connection.connectedAt) }}
                  </span>
                </div>

                <div class="meta-item">
                  <span class="meta-label">Last Sync</span>
                  <span class="meta-value">
                    {{ formatDate(connection.lastSyncAt) }}
                  </span>
                </div>

              </div>
            </div>

            <div class="connection-card-actions">
              <button
                class="action-btn view-emails-btn"
                (click)="toggleEmails(connection.id)"
                title="View emails">
                {{ selectedConnectionId === connection.id ? 'Hide Emails' : 'View Emails' }}
              </button>

              <button
                class="action-btn sync-emails-btn"
                (click)="syncEmails(connection.id)"
                [disabled]="syncingConnectionId === connection.id"
                title="Sync emails to blob storage">
                {{ syncingConnectionId === connection.id ? 'Syncing...' : 'Sync Emails' }}
              </button>

              <button
                *ngIf="connection.providerName === 'Outlook'"
                class="action-btn clear-mailbox-btn"
                (click)="clearMailbox(connection.id)"
                [disabled]="clearingMailboxId === connection.id"
                title="Clear all emails from blob storage, mailbox, and indexes">
                {{ clearingMailboxId === connection.id ? 'Clearing...' : 'Clear Mailbox' }}
              </button>

              <button
                class="action-btn test-btn"
                (click)="testConnection(connection.id)"
                title="Test connection">
                Test
              </button>

              <button
                class="action-btn disconnect-btn"
                (click)="deleteConnection(connection.id)"
                title="Disconnect">
                Disconnect
              </button>
            </div>

            <!-- Sync Status Indicator -->
            <div class="sync-status-bar" *ngIf="syncStatus && lastSyncConnectionId === connection.id && syncingConnectionId === connection.id">
              <div class="sync-spinner"></div>
              <span class="sync-status-text">{{ syncStatus }}</span>
            </div>

            <div class="sync-success-bar" *ngIf="syncStatus && lastSyncConnectionId === connection.id && syncError === null && syncingConnectionId !== connection.id">
              <span class="sync-success-icon">&#10003;</span>
              <span class="sync-success-text">{{ syncStatus }}</span>
            </div>

            <div class="sync-error-bar" *ngIf="syncError && lastSyncConnectionId === connection.id">
              <span class="sync-error-icon">&#9888;</span>
              <span class="sync-error-text">{{ syncError }}</span>
            </div>

            <!-- Clear Mailbox Status Indicator -->
            <div class="clear-status-bar" *ngIf="clearMailboxStatus && clearingMailboxId === connection.id">
              <div class="clear-spinner"></div>
              <span class="clear-status-text">{{ clearMailboxStatus }}</span>
            </div>

            <div class="clear-success-bar" *ngIf="clearMailboxStatus && lastClearConnectionId === connection.id && clearMailboxError === null && clearingMailboxId !== connection.id">
              <span class="clear-success-icon">&#10003;</span>
              <span class="clear-success-text">{{ clearMailboxStatus }}</span>
            </div>

            <div class="clear-error-bar" *ngIf="clearMailboxError && lastClearConnectionId === connection.id">
              <span class="clear-error-icon">&#9888;</span>
              <span class="clear-error-text">{{ clearMailboxError }}</span>
            </div>

            <!-- Email Panel (Expandable) -->
            <div class="email-panel" *ngIf="selectedConnectionId === connection.id">
              <div class="email-panel-header">
                <h3 class="email-panel-title">Emails</h3>
                <button class="email-panel-close" (click)="closeEmails()">&times;</button>
              </div>

              <!-- Email Loading -->
              <div class="email-loading" *ngIf="emailsLoading">
                <div class="loading-spinner small"></div>
                <p>Loading emails...</p>
              </div>

              <!-- Email Error -->
              <div class="email-error" *ngIf="!emailsLoading && emailsError">
                <p>{{ emailsError }}</p>
              </div>

              <!-- Email Empty -->
              <div class="email-empty" *ngIf="!emailsLoading && !emailsError && emails.length === 0">
                <p>No emails found.</p>
              </div>

              <!-- Email List -->
              <div class="email-list" *ngIf="!emailsLoading && !emailsError && emails.length > 0">
                <div class="email-item" *ngFor="let email of emails">
                  <div class="email-item-header">
                    <span class="email-subject">{{ email.subject || '(No Subject)' }}</span>
                    <span class="email-date">{{ email.receivedTime ? (email.receivedTime | date:'short') : '' }}</span>
                  </div>
                  <div class="email-item-meta">
                    <span class="email-from">From: {{ email.fromAddress }}</span>
                    <span class="email-attachment" *ngIf="email.hasAttachment">&#128206; Attachment</span>
                  </div>
                  <p class="email-summary">{{ email.summary }}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </div>

    <!-- CONNECTORS TAB -->
    <div *ngIf="activeTab === 'connectors'">

      <div class="mailbox-connectors-container">
        <div class="mailbox-connectors-header">
          <div class="mailbox-connectors-header-left">
            <h2 class="mailbox-connectors-title">Mailbox Connectors</h2>
          </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" *ngIf="connectorsLoading">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading available connectors...</p>
        </div>

        <!-- Error State -->
        <div class="error-state" *ngIf="!connectorsLoading && connectorsError">
          <div class="error-content">
            <div class="error-icon">&#9888;</div>
            <p class="error-message">{{ connectorsError }}</p>
            <button class="retry-button" (click)="loadProviders()">
              Retry
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state"
          *ngIf="!connectorsLoading && !connectorsError && providers.length === 0">

          <div class="empty-content">
            <div class="empty-icon">&#128229;</div>
            <h3 class="empty-title">No Connectors Available</h3>
            <p class="empty-message">
              No mailbox providers are configured yet. Please contact your administrator.
            </p>
          </div>

        </div>

        <!-- Providers Grid -->
        <div
          class="providers-grid"
          *ngIf="!connectorsLoading && !connectorsError && providers.length > 0">

          <div
            class="provider-card"
            *ngFor="let provider of providers">

            <div class="provider-card-header">
              <div class="provider-icon-wrapper">
                <span
                  class="provider-icon"
                  [innerHTML]="getProviderIcon(provider)">
                </span>
              </div>

              <h3 class="provider-name">
                {{ provider.name }}
              </h3>
            </div>

            <p
              class="provider-description"
              *ngIf="provider.description">
              {{ provider.description }}
            </p>

            <p
              class="provider-description"
              *ngIf="!provider.description">
              Connect your {{ provider.name }} account to enable mailbox integration.
            </p>

            <div class="provider-card-footer">

              <button
                *ngIf="isOAuthProvider(provider)"
                class="connect-btn oauth-btn"
                (click)="connectWithOAuth(provider)"
                [disabled]="isConnecting">

                <span *ngIf="isConnecting && selectedProvider?.id === provider.id">
                  Connecting...
                </span>

                <span *ngIf="!(isConnecting && selectedProvider?.id === provider.id)">
                  Connect with {{ provider.name }}
                </span>

              </button>

              <button
                *ngIf="!isOAuthProvider(provider)"
                class="connect-btn"
                (click)="openConnectModal(provider)">
                Connect
              </button>

            </div>

          </div>

        </div>
      </div>

    </div>

  </div>
</div>

<!-- Connection Modal -->
<div
  class="modal-overlay"
  *ngIf="showConnectionModal"
  (click)="closeConnectModal()">

  <div
    class="modal-content"
    (click)="$event.stopPropagation()">

    <div class="modal-header">
      <h3 class="modal-title">
        Connect {{ selectedProvider?.name || 'Mailbox' }}
      </h3>

      <button
        class="modal-close-btn"
        (click)="closeConnectModal()">
        &times;
      </button>
    </div>

    <div class="modal-body">

      <div class="form-group">
        <label
          class="form-label"
          for="email">
          Email Address
        </label>

        <input
          id="email"
          type="email"
          class="form-input"
          placeholder="user@example.com"
          [(ngModel)]="connectionEmail"
          [disabled]="isConnecting" />
      </div>

      <div class="form-group">
        <label
          class="form-label"
          for="displayName">
          Display Name (Optional)
        </label>

        <input
          id="displayName"
          type="text"
          class="form-input"
          placeholder="My Work Mailbox"
          [(ngModel)]="connectionDisplayName"
          [disabled]="isConnecting" />
      </div>

    </div>

    <div class="modal-footer">

      <button
        class="cancel-btn"
        (click)="closeConnectModal()"
        [disabled]="isConnecting">
        Cancel
      </button>

      <button
        class="confirm-btn"
        (click)="connectMailbox()"
        [disabled]="!connectionEmail.trim() || isConnecting">

        <span *ngIf="isConnecting">
          Connecting...
        </span>

        <span *ngIf="!isConnecting">
          Connect
        </span>

      </button>

    </div>
  </div>
</div>